import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Image,
    Card,
    Spinner,
    Alert,
    Button,
    Badge,
} from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

import SEO from '@/components/common/SEO';
import { fetchPublicProfile, fetchUserPosts } from '@/shared/services/userApi';
import { formatRelativeTime, formatFullDate } from '@/shared/utils/dateUtils';
import isSafeUrl from '@/shared/utils/urlHelpers';
import sustainabilityHubLogo from '@/assets/img/logos/png/blueBackground.png';

import './PublicProfile.css';

export default function PublicProfile() {
    const { username } = useParams();

    const [profile, setProfile] = useState(null);
    const [posts, setPosts] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [postsAvailable, setPostsAvailable] = useState(false);

    const [loading, setLoading] = useState(true);
    const [postsLoading, setPostsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);

    // Fetch the public profile
    useEffect(() => {
        let cancelled = false;

        async function loadProfile() {
            setLoading(true);
            setError(null);
            setNotFound(false);

            try {
                const data = await fetchPublicProfile(username);
                if (cancelled) return;
                setProfile(data);
            } catch (err) {
                if (cancelled) return;
                if (err.response?.status === 404) {
                    setNotFound(true);
                } else {
                    setError(
                        err.response?.data?.message || 'Failed to load profile.'
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadProfile();
        return () => {
            cancelled = true;
        };
    }, [username]);

    // Fetch posts once profile is loaded
    const loadPosts = useCallback(
        async (page = 1) => {
            setPostsLoading(true);
            try {
                const data = await fetchUserPosts(username, page);
                if (data.posts && data.posts.length > 0) {
                    setPosts(data.posts);
                    setPagination(data.pagination);
                    setPostsAvailable(true);
                } else {
                    setPostsAvailable(false);
                }
            } catch {
                // Posts not available — don't show broken UI
                setPostsAvailable(false);
            } finally {
                setPostsLoading(false);
            }
        },
        [username]
    );

    useEffect(() => {
        if (profile) {
            loadPosts(1);
        }
    }, [profile, loadPosts]);

    // --- Render helpers ---

    const getUserType = () => {
        if (!profile?.userType) return null;
        return (
            profile.userType.charAt(0).toUpperCase() + profile.userType.slice(1)
        );
    };

    const getProfilePicture = () => profile?.profilePicture || null;

    // --- Loading state ---
    if (loading) {
        return (
            <section
                id="public-profile"
                className="page-container py-5"
                aria-label="Public profile"
            >
                <Container className="text-center">
                    <Spinner animation="border" variant="primary" role="status">
                        <span className="visually-hidden">
                            Loading profile...
                        </span>
                    </Spinner>
                    <p className="mt-3 text-muted">Loading profile...</p>
                </Container>
            </section>
        );
    }

    // --- 404 state ---
    if (notFound) {
        return (
            <section
                id="public-profile"
                className="page-container py-5"
                aria-label="Public profile"
            >
                <SEO title="User Not Found" noindex />
                <Container className="text-center">
                    <h1 className="display-6 text-primary mb-3">
                        User Not Found
                    </h1>
                    <p className="text-muted mb-4">
                        The user <strong>@{username}</strong> does not exist or
                        their profile is not available.
                    </p>
                    <Link to="/" className="btn btn-primary">
                        Go Home
                    </Link>
                </Container>
            </section>
        );
    }

    // --- Error state ---
    if (error) {
        return (
            <section
                id="public-profile"
                className="page-container py-5"
                aria-label="Public profile"
            >
                <Container>
                    <Alert variant="danger" className="text-center">
                        <Alert.Heading>Something went wrong</Alert.Heading>
                        <p>{error}</p>
                        <Button
                            variant="outline-danger"
                            onClick={() => window.location.reload()}
                        >
                            Try Again
                        </Button>
                    </Alert>
                </Container>
            </section>
        );
    }

    // --- Profile loaded ---
    return (
        <section
            id="public-profile"
            className="page-container py-4"
            aria-label="Public profile"
        >
            <SEO
                title={`@${profile.username}`}
                description={
                    profile.bio ||
                    `Public profile of @${profile.username} on Sustainability Hub`
                }
                canonical={`/users/${profile.username}`}
            />
            <Container>
                {/* Profile Header */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="p-4">
                        <Row className="align-items-center">
                            <Col
                                xs={12}
                                md="auto"
                                className="text-center mb-3 mb-md-0"
                            >
                                {getProfilePicture() ? (
                                    <Image
                                        src={getProfilePicture()}
                                        alt={`${profile.username} profile picture`}
                                        roundedCircle
                                        className="profile-avatar"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src =
                                                sustainabilityHubLogo;
                                        }}
                                    />
                                ) : (
                                    <div className="profile-avatar-fallback rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mx-auto">
                                        {(
                                            profile.username?.[0] || '?'
                                        ).toUpperCase()}
                                    </div>
                                )}
                            </Col>
                            <Col>
                                <h1 className="h3 text-primary mb-1">
                                    @{profile.username}
                                </h1>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    {getUserType() && (
                                        <Badge bg="primary">
                                            {getUserType()}
                                        </Badge>
                                    )}
                                    {profile.organization && (
                                        <Badge bg="secondary">
                                            <i
                                                className="bi bi-building me-1"
                                                aria-hidden="true"
                                            />
                                            {profile.organization}
                                        </Badge>
                                    )}
                                    {profile.joinDate && (
                                        <span className="text-muted small">
                                            <i
                                                className="bi bi-calendar3 me-1"
                                                aria-hidden="true"
                                            />
                                            Joined{' '}
                                            {new Date(
                                                profile.joinDate
                                            ).toLocaleDateString('en-US', {
                                                month: 'long',
                                                year: 'numeric',
                                            })}
                                        </span>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Bio & Details */}
                {(profile.bio ||
                    profile.interests?.length > 0 ||
                    profile.externalLinks?.length > 0) && (
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="p-4">
                            {profile.bio && (
                                <div className="mb-3">
                                    <h2 className="h6 fw-bold text-primary">
                                        About
                                    </h2>
                                    <p className="mb-0">{profile.bio}</p>
                                </div>
                            )}

                            {profile.interests?.length > 0 && (
                                <div className="mb-3">
                                    <h2 className="h6 fw-bold text-primary">
                                        Interests
                                    </h2>
                                    <div className="d-flex flex-wrap gap-2">
                                        {profile.interests.map(
                                            (interest, i) => (
                                                <Badge
                                                    key={`${interest}-${i}`}
                                                    bg="light"
                                                    text="dark"
                                                    className="border"
                                                >
                                                    {interest}
                                                </Badge>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}

                            {profile.externalLinks?.length > 0 && (
                                <div>
                                    <h2 className="h6 fw-bold text-primary">
                                        Links
                                    </h2>
                                    <ul className="list-unstyled mb-0">
                                        {profile.externalLinks.map(
                                            (link, i) => (
                                                <li key={`${link}-${i}`}>
                                                    {isSafeUrl(link) ? (
                                                        <a
                                                            href={link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none"
                                                        >
                                                            <i
                                                                className="bi bi-link-45deg me-1"
                                                                aria-hidden="true"
                                                            />
                                                            {link}
                                                        </a>
                                                    ) : (
                                                        <span className="text-muted">
                                                            <i
                                                                className="bi bi-link-45deg me-1"
                                                                aria-hidden="true"
                                                            />
                                                            {link}
                                                        </span>
                                                    )}
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}

                {/* Engagement Metrics */}
                {profile.metrics && (
                    <Row className="g-3 mb-4">
                        <Col xs={4}>
                            <Card className="border-0 shadow-sm text-center">
                                <Card.Body className="py-3">
                                    <div className="h4 mb-0 text-primary">
                                        {profile.metrics.postCount}
                                    </div>
                                    <small className="text-muted">Posts</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={4}>
                            <Card className="border-0 shadow-sm text-center">
                                <Card.Body className="py-3">
                                    <div className="h4 mb-0 text-primary">
                                        {profile.metrics.commentCount}
                                    </div>
                                    <small className="text-muted">
                                        Comments
                                    </small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={4}>
                            <Card className="border-0 shadow-sm text-center">
                                <Card.Body className="py-3">
                                    <div className="h4 mb-0 text-primary">
                                        {profile.metrics.likesReceived}
                                    </div>
                                    <small className="text-muted">
                                        Likes Received
                                    </small>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Posts Section */}
                {postsLoading && (
                    <div className="text-center py-4" role="status">
                        <Spinner
                            animation="border"
                            variant="primary"
                            size="sm"
                        />
                        <span className="ms-2 text-muted">
                            Loading posts...
                        </span>
                    </div>
                )}

                {postsAvailable && !postsLoading && (
                    <>
                        <h2 className="h5 text-primary mb-3">
                            <i
                                className="bi bi-file-text me-2"
                                aria-hidden="true"
                            />
                            Posts
                        </h2>
                        {posts.map((post) => (
                            <Card
                                key={post._id}
                                className="post-card border-0 shadow-sm mb-3"
                            >
                                <Card.Body className="p-3">
                                    {post.title && (
                                        <h3 className="h6 fw-bold mb-2">
                                            {post.title}
                                        </h3>
                                    )}
                                    <div className="text-muted mb-2 post-content-preview">
                                        <ReactMarkdown>
                                            {post.content?.length > 200
                                                ? `${post.content.substring(0, 200)}...`
                                                : post.content}
                                        </ReactMarkdown>
                                    </div>
                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                        <span
                                            title={formatFullDate(
                                                post.createdAt
                                            )}
                                        >
                                            {formatRelativeTime(post.createdAt)}
                                        </span>
                                        {post.likeCount > 0 && (
                                            <span>
                                                <i
                                                    className="bi bi-heart me-1"
                                                    aria-hidden="true"
                                                />
                                                {post.likeCount}
                                            </span>
                                        )}
                                        {post.commentCount > 0 && (
                                            <span>
                                                <i
                                                    className="bi bi-chat me-1"
                                                    aria-hidden="true"
                                                />
                                                {post.commentCount}
                                            </span>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        ))}

                        {/* Simple pagination */}
                        {pagination && pagination.pages > 1 && (
                            <nav
                                aria-label="Posts pagination"
                                className="d-flex justify-content-center gap-2 mt-3"
                            >
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    disabled={pagination.page <= 1}
                                    onClick={() =>
                                        loadPosts(pagination.page - 1)
                                    }
                                    aria-label={`Go to previous page, currently page ${pagination.page} of ${pagination.pages}`}
                                >
                                    Previous
                                </Button>
                                <span
                                    className="d-flex align-items-center text-muted small"
                                    aria-current="page"
                                >
                                    Page {pagination.page} of {pagination.pages}
                                </span>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    disabled={
                                        pagination.page >= pagination.pages
                                    }
                                    onClick={() =>
                                        loadPosts(pagination.page + 1)
                                    }
                                    aria-label={`Go to next page, currently page ${pagination.page} of ${pagination.pages}`}
                                >
                                    Next
                                </Button>
                            </nav>
                        )}
                    </>
                )}
            </Container>
        </section>
    );
}
